import type { Options } from '@/models/option';
import { calcPriceIncludingTax } from '@/utils/priceIncludingTax';

import type { WabunkaExperienceEventExperienceEventInterpreterOption } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_experience_event_interpreter_option';
import type { WabunkaExperienceEventInterpreterOption } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_interpreter_option';

/**
 * 通訳オプションビューモデル
 */
export interface WabunkaExperienceEventInterpreterOptionViewModel {
  /** 通訳オプションマスタID */
  id: string;
  /** 通訳オプション名 */
  name: string;
  /** 説明 */
  description: string;
  /** 金額（税抜） */
  price: number;
  /** 税率 */
  taxRate: number;
  /** 金額(税込) */
  priceIncludingTax: number;
  /** シーケンスID */
  sequenceId: number;
}

/**
 * 通訳オプションビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaInterpreterOptionViewModel} - 通訳オプションビューモデルのデフォルト値
 */
export const createDefaultWabunkaExperienceEventInterpreterOptionViewModel = (): WabunkaExperienceEventInterpreterOptionViewModel => ({
  id: '',
  name: '',
  description: '',
  price: 0,
  taxRate: 0,
  priceIncludingTax: 0,
  sequenceId: 1,
});

/**
 * セレクトボックス用配列の組み立て
 * @param {WabunkaExperienceEventInterpreterOptionViewModel} experienceEventInterpreterOptions - 通訳オプションビューモデル
 * @returns {Options} - 選択肢の配列
 */
export const buildWabunkaExperienceEventInterpreterOptionChoices = (experienceEventInterpreterOptions: WabunkaExperienceEventInterpreterOptionViewModel[], isPublicPrice: boolean): Options => {
  if (!experienceEventInterpreterOptions) return [];

  return experienceEventInterpreterOptions
    .sort((a, b) => {
      // シーケンスIDの昇順でソート
      return a.sequenceId - b.sequenceId;
    })
    .map((_) => {
      if (isPublicPrice) {
        return { label: `${_.name}（Paid : +JPY ${_.priceIncludingTax}）`, value: _.id };
      }
      return { label: `${_.name}`, value: _.id };
    });
};

/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {WabunkaExperienceEventExperienceEventInterpreterOption[]} res - APIレスポンス
 * @returns {WabunkaExperienceEventInterpreterOptionViewModel[]} - ビューモデル
 */
export const buildWabunkaExperienceEventInterpreterOptionListViewModel = (res: WabunkaExperienceEventExperienceEventInterpreterOption[]): WabunkaExperienceEventInterpreterOptionViewModel[] =>
  res
    .filter((experienceEventInterpreterOption: WabunkaExperienceEventExperienceEventInterpreterOption) => {
      return experienceEventInterpreterOption.experienceEventInterpreterOption !== undefined;
    })
    .map((experienceEventInterpreterOption: WabunkaExperienceEventExperienceEventInterpreterOption): WabunkaExperienceEventInterpreterOptionViewModel => {
      const interpreterOption = experienceEventInterpreterOption.experienceEventInterpreterOption as WabunkaExperienceEventInterpreterOption;
      return {
        id: interpreterOption.id,
        name: interpreterOption.name,
        description: interpreterOption.description,
        price: interpreterOption.price,
        taxRate: interpreterOption.taxRate,
        priceIncludingTax: calcPriceIncludingTax(interpreterOption.price, interpreterOption.taxRate),
        sequenceId: experienceEventInterpreterOption.sequenceId,
      };
    })
    .sort((a, b) => a.sequenceId - b.sequenceId);
